import { CHAT } from 'constants.js'
import { fetchChannelMessages } from 'api/api'

import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { getShowChannel } from './interaction'
import moment from 'moment'
import { postChatMessage } from 'api/api'
import uuid from 'uuid'
import { Message } from 'data/Chat'
import { AppThunk, RootState } from 'redux/store'
import { User } from 'data/User'

interface ChatState {
    messages: Message[]
    newMessages: Message[]
    earliestTimestamp: string
    scrollState: 'sync' | 'history'
}

export const initialState: ChatState = {
    messages: [],
    newMessages: [],
    earliestTimestamp: '',
    scrollState: 'sync',
}

const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        resetState: _ => initialState,
        addNewMessage: (state, { payload: message }: PayloadAction<Message>) => {
            if (state.scrollState === 'sync') {
                state.messages = appendMessage(message, state.messages)
                setEarliestTimestamp(state.messages, state)
            } else {
                state.newMessages = appendMessage(message, state.newMessages)
            }
        },
        messagesReceived: (state, { payload: message }: PayloadAction<Message[]>) => {
            state.messages = message
            setEarliestTimestamp(message, state)
        },
        addHistoryToMessages: (state, { payload: messages }: PayloadAction<Message[]>) => {
            state.messages = messages.concat(state.messages)
            setEarliestTimestamp(messages, state)
        },
        syncMessages: state => {
            state.scrollState = 'sync'
            state.messages = [...state.messages, ...state.newMessages]
            state.newMessages = []
        },
        browseMessageHistory: state => {
            state.scrollState = 'history'
        },
    },
})

const setEarliestTimestamp = ([head]: Message[], state: ChatState) => {
    const timestamp = head && head.timestamp
    if (timestamp) {
        state.earliestTimestamp = timestamp
    }
}

const appendMessage = (msg: Message, list: Message[]) => {
    const newList = list.length >= CHAT.IN_MEMORY_MESSAGE_LIMIT ? list.slice(1) : list
    return [...newList, msg]
}

const { actions, reducer } = chatSlice

export const {
    resetState,
    addHistoryToMessages,
    addNewMessage,
    messagesReceived,
    syncMessages,
    browseMessageHistory,
} = actions

export default reducer

export const getChatState = (store: RootState) => store.chat
export const getMessages = createSelector(getChatState, chat => chat.messages)
export const getScrollState = createSelector(getChatState, chat => chat.scrollState)

export const retrieveInitialMessages = (): AppThunk => async (dispatch, getState) => {
    const showChannel = getShowChannel(getState())

    if (!showChannel) {
        console.error(`We can't retrieve the Chat Initial Messages because the showChannel is undefined`)
        return
    }

    const messages = await fetchChannelMessages(showChannel.name, CHAT.IN_MEMORY_MESSAGE_LIMIT, moment())
    dispatch(messagesReceived(messages))
}

export const retrieveMessageHistory = (): AppThunk => async (dispatch, getState) => {
    const showChannel = getShowChannel(getState())

    if (!showChannel) {
        console.error(`We can't retrieve the Chat Message History because the showChannel is undefined`)
        return
    }

    const messageHistory = await fetchChannelMessages(
        showChannel.name,
        CHAT.MESSAGE_REFRESH_FETCH_COUNT,
        getState().chat.earliestTimestamp,
    )

    if (messageHistory && messageHistory.length) {
        dispatch(addHistoryToMessages(messageHistory))
    }
}

export const sendMessage = (message: Message, user: User): AppThunk => async (dispatch, getState) => {
    const showChannel = getShowChannel(getState())

    if (!showChannel) {
        console.error(`We can't send the chat message because the showChannel is undefined`)
        return
    }

    const enrichedMessage = {
        ...message,
        channel: showChannel.name,
        socketId: showChannel.pusher.connection.socket_id,
    }

    dispatch(
        addNewMessage({
            ...enrichedMessage,
            msgId: uuid(),
            displayName: user.displayName,
            userId: user._id,
        }),
    )

    return postChatMessage(enrichedMessage)
}
